import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  useEventCallback,
} from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import Italian from '../../shared/italian';
import Foreign from '../../shared/foreign';
import ChooseDate from 'shell/components/shared/choice-date';
import { FormikErrors, FormikTouched } from 'formik';
import { ItalianForm } from 'shell/view-models/italian-form';
import { ForeignForm } from 'shell/view-models/foreign-form';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { DateTime } from 'luxon';
import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { AccordionWidowhood } from 'shell/components/shared/sipo-person-form/new-person';
import PmiDivider from 'shell/components/shared/divider';

interface FormWidowhoodProps {
  errors?: FormikErrors<AccordionWidowhood>;
  touched?: FormikTouched<AccordionWidowhood>;
  values?: AccordionWidowhood;
  onChange: (
    key: keyof AccordionWidowhood,
    value: AccordionWidowhood[keyof AccordionWidowhood]
  ) => void;
  onChangeValues: (values: AccordionWidowhood) => void;
}

const FormWidowhoodComponent = ({
  values,
  onChange,
  onChangeValues,
  errors,
  touched,
}: FormWidowhoodProps): React.ReactElement => {
  const onFieldItalianChange = useCallback(
    (key, value) => {
      onChange('italian', {
        ...(values?.italian || {}),
        [key]: value,
      });
    },
    [values?.italian, onChange]
  );

  const onFieldForeignChange = useCallback(
    (key, value) => {
      onChange('foreign', {
        ...(values?.foreign || {}),
        [key]: value,
      });
    },
    [values?.foreign, onChange]
  );

  const handleMunicipalityListChange = useEventCallback(
    (municipality: MunicipalityAnprVM | null) => {
      onChange('municipality', municipality);
    }
  );

  const onChooseDateChange = (value: DateTime | null, choice?: Array<DatePickerView>) =>
    onChangeValues({
      eventDate: value,
      eventChoiceDate: choice,
    });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <PmiTextField
          label="Numero matrimonio"
          aria-label="Numero matrimonio"
          id="number-marriage"
          fullWidth
          inputProps={{
            maxLength: '2',
          }}
          value={values?.number || ''}
          onChange={(event) => {
            onChange('number', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography>Dati decesso del coniuge</Typography>
      </Grid>
      <Grid item xs={12} container spacing={2} alignItems="center">
        <ChooseDate
          selected={values?.eventDate || null}
          onChange={onChooseDateChange}
          required
          choice={values?.eventChoiceDate}
          label="Data evento"
          error={Boolean(errors?.eventDate) && touched?.eventDate}
          helperText={errors?.eventDate}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Comune</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Evento"
            id="isForeign"
            value={values?.isForeign || 'false'}
            onChange={() => {
              onChangeValues({
                ...values,
                isForeign: !values?.isForeign,
                italian: {
                  location: '',
                  province: '',
                  municipality: null,
                },
                foreign: {
                  state: null,
                  province: '',
                  municipality: null,
                },
              });
              return;
            }}
          >
            <FormControlLabel value="false" control={<Radio />} label="Evento Italia" />
            <FormControlLabel value="true" control={<Radio />} label="Evento Stato Estero" />
          </RadioGroup>
        </FormControl>
      </Grid>
      {!values?.isForeign ? (
        <Italian
          values={values?.italian}
          onChange={onFieldItalianChange}
          errors={errors?.italian as FormikErrors<ItalianForm>}
          touched={touched?.italian as FormikTouched<ItalianForm>}
          labelMunicipality="Comune evento"
          labelProvince="Provincia evento"
          eventDate={values?.eventDate}
        />
      ) : (
        <Foreign
          values={values?.foreign}
          onChange={onFieldForeignChange}
          errors={errors?.foreign as FormikErrors<ForeignForm>}
          touched={touched?.foreign as FormikTouched<ForeignForm>}
          labelMunicipality="Comune evento"
          labelState="Stato evento"
        />
      )}
      <Grid item xs={12}>
        <PmiTextField
          label="Luogo eccezionale"
          aria-label="Luogo eccezionale"
          id="exceptional-location"
          fullWidth
          inputProps={{
            maxLength: '120',
          }}
          value={values?.exceptionalLocation || ''}
          error={touched?.exceptionalLocation && Boolean(errors?.exceptionalLocation)}
          helperText={errors?.exceptionalLocation}
          onChange={(event) => {
            onChange('exceptionalLocation', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Atto</PmiTitleDivider>
      </Grid>
      <Grid item container spacing={2} xs={12} alignItems="center">
        <Grid item xs={12} md={6}>
          <DatePicker
            label="Data atto"
            aria-label="Data atto"
            value={values?.documentDate || null}
            onChange={(date) => {
              onChange('documentDate', date);
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="documentDate"
                fullWidth
                error={touched?.documentDate && Boolean(errors?.documentDate)}
                helperText={errors?.documentDate}
              />
            )}
          />
        </Grid>
        <Grid item xs>
          <FormControlLabel
            control={
              <Checkbox
                id="documentTranscript"
                checked={values?.transcript || false}
                onChange={() => onChange('transcript', !values?.transcript)}
                inputProps={{ 'aria-label': 'Atto trascritto' }}
              />
            }
            label="Atto trascritto"
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={9}>
        <MunicipalityList
          label="Comune"
          aria-label="Comune"
          value={values?.municipality || null}
          error={touched?.municipality && Boolean(errors?.municipality)}
          onChange={handleMunicipalityListChange}
          fromDate={values?.documentDate}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          label="Provincia"
          id="province"
          inputProps={{
            maxLength: '2',
          }}
          aria-label="Provincia"
          fullWidth
          value={values?.municipality?.province || ''}
          onChange={(event) => {
            onChange('province', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Anno"
          id="year"
          aria-label="Anno"
          fullWidth
          inputProps={{
            maxLength: '4',
          }}
          value={values?.year || ''}
          error={touched?.year && Boolean(errors?.year)}
          helperText={errors?.year}
          onChange={(event) => {
            onChange('year', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Nr.Atto"
          aria-label="Nr.Atto"
          id="number-document"
          fullWidth
          inputProps={{
            maxLength: '20',
          }}
          value={values?.documentNumber || ''}
          error={touched?.documentNumber && Boolean(errors?.documentNumber)}
          helperText={errors?.documentNumber}
          onChange={(event) => {
            onChange('documentNumber', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Parte"
          aria-label="Parte"
          id="portion"
          fullWidth
          inputProps={{
            maxLength: '5',
          }}
          value={values?.portion || ''}
          error={touched?.portion && Boolean(errors?.portion)}
          helperText={errors?.portion}
          onChange={(event) => {
            onChange('portion', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Serie"
          aria-label="Serie"
          id="range"
          fullWidth
          inputProps={{
            maxLength: '10',
          }}
          value={values?.range || ''}
          error={touched?.range && Boolean(errors?.range)}
          helperText={errors?.range}
          onChange={(event) => {
            onChange('range', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          label="Reg/Vol"
          aria-label="Reg/Vol"
          id="reg-vol"
          fullWidth
          inputProps={{
            maxLength: '5',
          }}
          value={values?.regVol || ''}
          error={touched?.regVol && Boolean(errors?.regVol)}
          helperText={errors?.regVol}
          onChange={(event) => {
            onChange('regVol', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          label="Ufficio municipio"
          aria-label="Ufficio municipio"
          id="office"
          fullWidth
          inputProps={{
            maxLength: '50',
          }}
          value={values?.office || ''}
          error={touched?.office && Boolean(errors?.office)}
          helperText={errors?.office}
          onChange={(event) => {
            onChange('office', event.target.value);
          }}
        />
      </Grid>
    </Grid>
  );
};

FormWidowhoodComponent.displayName = 'Form widowhood component';

const FormWidowhood = memo(FormWidowhoodComponent);

export default FormWidowhood;
